// Content-pack assembly and validation. The engine holds no historical
// constants; everything era-shaped arrives through here. No file I/O in this
// module — callers (harness, tests) load JSON and pass it in.

import type {
  AskDef,
  ContentPack,
  NodeDef,
  MarketDef,
  EconomyDef,
  IpDef,
  RivalsContent,
  ScenarioDef,
  CurvePoint,
  HistoryEventDef,
  TechTreeDef,
} from "./types.js";
import { ATTR_KEYS, DISCIPLINES, STAGE_ORDER } from "./types.js";

export function buildContentPack(parts: {
  version: string;
  nodes: NodeDef[];
  markets: MarketDef[];
  economy: EconomyDef;
  scenario: ScenarioDef;
  events?: HistoryEventDef[];
  rivals: RivalsContent;
  asks?: AskDef[];
  ip?: IpDef[];
  trees?: TechTreeDef[];
}): ContentPack {
  const pack: ContentPack = {
    version: parts.version,
    nodes: [...parts.nodes].sort((a, b) => a.availableMonth - b.availableMonth),
    markets: parts.markets,
    economy: parts.economy,
    scenario: parts.scenario,
    events: [...(parts.events ?? [])].sort((a, b) => a.month - b.month),
    rivals: parts.rivals,
    asks: parts.asks ?? [],
    ip: parts.ip ?? [],
    trees: parts.trees ?? [],
  };
  validateContentPack(pack);
  return pack;
}

function assertCurve(curve: CurvePoint[], name: string, min?: number, max?: number): void {
  if (!Array.isArray(curve) || curve.length === 0) throw new Error(`${name}: empty curve`);
  for (let i = 0; i < curve.length; i++) {
    const p = curve[i]!;
    if (!Number.isFinite(p.m) || !Number.isFinite(p.v)) throw new Error(`${name}[${i}]: non-finite`);
    if (i > 0 && p.m <= curve[i - 1]!.m) throw new Error(`${name}: non-increasing m at index ${i}`);
    if (min !== undefined && p.v < min) throw new Error(`${name}[${i}]: value ${p.v} < ${min}`);
    if (max !== undefined && p.v > max) throw new Error(`${name}[${i}]: value ${p.v} > ${max}`);
  }
}

function assertPositive(x: number, name: string): void {
  if (!Number.isFinite(x) || x <= 0) throw new Error(`${name}: expected positive finite, got ${x}`);
}

export function validateContentPack(pack: ContentPack): void {
  if (!pack.version) throw new Error("content pack missing version");
  if (pack.nodes.length === 0) throw new Error("no nodes");
  if (pack.markets.length === 0) throw new Error("no markets");

  const nodeIds = new Set<string>();
  for (const n of pack.nodes) {
    if (nodeIds.has(n.id)) throw new Error(`duplicate node id ${n.id}`);
    nodeIds.add(n.id);
    assertPositive(n.waferDiameterMm, `${n.id}.waferDiameterMm`);
    assertPositive(n.waferCostStart, `${n.id}.waferCostStart`);
    assertPositive(n.waferCostMature, `${n.id}.waferCostMature`);
    assertPositive(n.waferCostTauMonths, `${n.id}.waferCostTauMonths`);
    assertPositive(n.maskSetCost, `${n.id}.maskSetCost`);
    assertPositive(n.d0Intro, `${n.id}.d0Intro`);
    assertPositive(n.d0Mature, `${n.id}.d0Mature`);
    if (n.d0Mature > n.d0Intro) throw new Error(`${n.id}: d0Mature > d0Intro`);
    assertPositive(n.d0TauMonths, `${n.id}.d0TauMonths`);
    assertPositive(n.gateDensity, `${n.id}.gateDensity`);
    assertPositive(n.qual.fee, `${n.id}.qual.fee`);
    assertPositive(n.qual.engMonths, `${n.id}.qual.engMonths`);
    assertPositive(n.qual.durationMonths, `${n.id}.qual.durationMonths`);
    assertPositive(n.fabCycleMonths, `${n.id}.fabCycleMonths`);
    if (n.availableMonth < 0) throw new Error(`${n.id}: negative availableMonth`);
  }
  for (let i = 1; i < pack.nodes.length; i++) {
    if (pack.nodes[i]!.availableMonth < pack.nodes[i - 1]!.availableMonth) {
      throw new Error("nodes must be sorted by availableMonth");
    }
  }

  const marketIds = new Set<string>();
  for (const mk of pack.markets) {
    if (marketIds.has(mk.id)) throw new Error(`duplicate market id ${mk.id}`);
    marketIds.add(mk.id);
    if (!["digital", "analog", "neutral"].includes(mk.pathAffinity)) {
      throw new Error(`${mk.id}.pathAffinity must be digital|analog|neutral`);
    }
    assertCurve(mk.tamUnitsPerMonth, `${mk.id}.tamUnitsPerMonth`, 0);
    assertCurve(mk.refAsp, `${mk.id}.refAsp`, 0);
    for (const [k, lo, hi] of [
      ["baseCaptureShare", 0, 1],
      ["shareRampMonthly", 0, 1],
      ["staleDecayMonthly", 0, 1],
      ["nodeSensitivity", 0, 1],
      ["totalShareCap", 0, 1],
    ] as const) {
      const v = mk[k];
      if (!(v >= lo && v <= hi)) throw new Error(`${mk.id}.${k}: ${v} outside [${lo},${hi}]`);
    }
    assertPositive(mk.productStaleMonths, `${mk.id}.productStaleMonths`);
    assertPositive(mk.expectedKGatesBase, `${mk.id}.expectedKGatesBase`);
    assertPositive(mk.expectedKGatesDoublingMonths, `${mk.id}.expectedKGatesDoublingMonths`);
    assertPositive(mk.complexityFitExp, `${mk.id}.complexityFitExp`);
    if (!(mk.complexityFitMin > 0 && mk.complexityFitMin <= mk.complexityFitMax)) {
      throw new Error(`${mk.id}: bad complexityFit bounds`);
    }
    if (mk.unitsNoiseSigma < 0) throw new Error(`${mk.id}.unitsNoiseSigma negative`);
    if (mk.testCostPerUnit < 0 || mk.packageCostPerUnit < 0) throw new Error(`${mk.id}: negative unit costs`);
    if (!(mk.rivalryRef >= 0 && mk.rivalryRef < 20)) throw new Error(`${mk.id}.rivalryRef out of range`);
    if (mk.winRollSigma !== undefined && !(mk.winRollSigma > 0 && mk.winRollSigma < 1)) {
      throw new Error(`${mk.id}.winRollSigma out of range`);
    }
    if (mk.aspQualityRef !== undefined && !(mk.aspQualityRef > 0.2 && mk.aspQualityRef <= 1.5)) {
      throw new Error(`${mk.id}.aspQualityRef out of range`);
    }
    if (mk.attrWeights !== undefined) {
      let any = false;
      for (const [k, v] of Object.entries(mk.attrWeights)) {
        if (!ATTR_KEYS.includes(k as (typeof ATTR_KEYS)[number])) throw new Error(`${mk.id}.attrWeights: unknown dimension ${k}`);
        if (!(typeof v === "number" && Number.isFinite(v) && v >= 0)) throw new Error(`${mk.id}.attrWeights.${k} invalid`);
        if (v > 0) any = true;
      }
      if (!any) throw new Error(`${mk.id}.attrWeights: all-zero weights`);
    }
  }

  const eco = pack.economy;
  if (eco.salaries.length === 0) throw new Error("empty salary table");
  for (let i = 0; i < eco.salaries.length; i++) {
    const s = eco.salaries[i]!;
    if (!Number.isFinite(s.m)) throw new Error(`salaries[${i}].m non-finite`);
    if (i > 0 && s.m <= eco.salaries[i - 1]!.m) throw new Error(`salaries: non-increasing m at index ${i}`);
    for (const d of DISCIPLINES) assertPositive(s[d], `salaries@${s.m}.${d}`);
  }
  assertPositive(eco.overheadBurden, "overheadBurden");
  assertCurve(eco.toolCostPerEngYear, "toolCostPerEngYear", 0);
  assertCurve(eco.gnaBaseMonthly, "gnaBaseMonthly", 0);
  assertCurve(eco.sentiment, "sentiment", 0, 1);
  assertCurve(eco.eraCapitalScale, "eraCapitalScale", 0);
  if (eco.attritionMonthly < 0 || eco.attritionMonthly > 0.1) throw new Error("attritionMonthly out of range");
  assertPositive(eco.hireRampMonths, "hireRampMonths");
  if (eco.rampEffectiveness < 0 || eco.rampEffectiveness > 1) throw new Error("rampEffectiveness out of range");

  assertCurve(eco.nre.billRatePerEngMonth, "nre.billRatePerEngMonth", 0);
  if (!Number.isInteger(eco.nre.maxEngineers) || eco.nre.maxEngineers < 0) {
    throw new Error("nre.maxEngineers must be a non-negative integer");
  }

  const fin = eco.financing;
  for (const [k, v] of Object.entries({ pBase: fin.pBase, pSlope: fin.pSlope, pMin: fin.pMin, pMax: fin.pMax })) {
    if (!(v >= 0 && v <= 1)) throw new Error(`financing.${k}: ${v} outside [0,1]`);
  }
  if (fin.pMin > fin.pMax) throw new Error("financing.pMin > pMax");

  // ---- M2 sections ----
  const ts = eco.termSheets;
  assertPositive(ts.lifeMonths, "termSheets.lifeMonths");
  if (!(ts.poolTopUpEarly >= 0 && ts.poolTopUpEarly < 0.5)) throw new Error("termSheets.poolTopUpEarly out of range");
  if (!(ts.poolTopUpLate >= 0 && ts.poolTopUpLate < 0.5)) throw new Error("termSheets.poolTopUpLate out of range");
  if (!Number.isInteger(ts.earlyRounds) || ts.earlyRounds < 0) throw new Error("termSheets.earlyRounds must be a non-negative integer");
  assertPositive(ts.boardSeatMinAmount, "termSheets.boardSeatMinAmount");
  if (ts.valuationSigma < 0) throw new Error("termSheets.valuationSigma negative");
  for (const k of ["confHighBonus", "confLowHaircut", "confCrisisHaircut"] as const) {
    assertPositive(ts[k], `termSheets.${k}`);
  }

  const bd = eco.board;
  if (!(bd.start >= 0 && bd.start <= 100)) throw new Error("board.start outside [0,100]");
  if (!(bd.baseline >= 0 && bd.baseline <= 100)) throw new Error("board.baseline outside [0,100]");
  if (!(bd.meanReversionMonthly >= 0 && bd.meanReversionMonthly <= 1)) throw new Error("board.meanReversionMonthly outside [0,1]");
  if (!(bd.ousterFloor < bd.ultimatumFloor && bd.ultimatumFloor < bd.concernFloor)) {
    throw new Error("board floors must satisfy ouster < ultimatum < concern");
  }
  if (!Number.isInteger(bd.ousterMinInvestorSeats) || bd.ousterMinInvestorSeats < 1) {
    throw new Error("board.ousterMinInvestorSeats must be a positive integer");
  }
  for (const [k, v] of Object.entries(bd.deltas)) {
    if (!Number.isFinite(v)) throw new Error(`board.deltas.${k} non-finite`);
  }

  const ipo = eco.ipo;
  assertCurve(ipo.window, "ipo.window", 0, 1);
  assertCurve(ipo.minTtmRevenue, "ipo.minTtmRevenue", 0);
  assertCurve(ipo.reqProfitQuarters, "ipo.reqProfitQuarters", 0, 8);
  assertCurve(ipo.s1Cost, "ipo.s1Cost", 0);
  if (!(ipo.pullFloor >= 0 && ipo.pullFloor < ipo.windowFloorToFile)) {
    throw new Error("ipo.pullFloor must be in [0, windowFloorToFile) or every filing is pulled at once");
  }
  assertPositive(ipo.s1Months, "ipo.s1Months");
  assertPositive(ipo.roadshowMonths, "ipo.roadshowMonths");
  assertPositive(ipo.pricingMaxMonths, "ipo.pricingMaxMonths");
  if (!(ipo.roadshowProductivityHit >= 0 && ipo.roadshowProductivityHit < 1)) throw new Error("ipo.roadshowProductivityHit outside [0,1)");
  if (!(ipo.newSharesFrac > 0 && ipo.newSharesFrac < 0.5)) throw new Error("ipo.newSharesFrac must be in (0,0.5)");
  if (!(ipo.pricingRangeSpread > 0 && ipo.pricingRangeSpread < 1)) throw new Error("ipo.pricingRangeSpread out of range");
  assertPositive(ipo.lockupMonths, "ipo.lockupMonths");
  if (!(ipo.lockupPressure > 0 && ipo.lockupPressure <= 1)) throw new Error("ipo.lockupPressure must be in (0,1]");
  assertPositive(ipo.cooldownMonths, "ipo.cooldownMonths");
  if (ipo.firstDaySigma < 0) throw new Error("ipo.firstDaySigma negative");
  for (const tier of ["bulge", "boutique"] as const) {
    const uw = ipo.underwriters[tier];
    if (!uw) throw new Error(`ipo.underwriters.${tier} missing`);
    if (!(uw.feePct > 0 && uw.feePct < 0.2)) throw new Error(`ipo.underwriters.${tier}.feePct out of range`);
    assertPositive(uw.multBonus, `ipo.underwriters.${tier}.multBonus`);
    if (!(uw.credStart > 0 && uw.credStart <= 1)) throw new Error(`ipo.underwriters.${tier}.credStart out of range`);
  }

  const st = eco.street;
  assertCurve(st.psMultiple, "street.psMultiple", 0);
  assertCurve(st.peMultiple, "street.peMultiple", 0);
  assertCurve(st.publicCostMonthly, "street.publicCostMonthly", 0);
  if (!(st.priceReversion > 0 && st.priceReversion <= 1)) throw new Error("street.priceReversion must be in (0,1]");
  if (st.priceSigma < 0) throw new Error("street.priceSigma negative");
  if (!(st.missThreshold < st.beatThreshold)) throw new Error("street.missThreshold must be < beatThreshold");
  if (!(st.missPricePenalty >= 0 && st.missPricePenalty < 1)) throw new Error("street.missPricePenalty outside [0,1)");
  if (!(st.guideGrowthClampLo <= 1 && st.guideGrowthClampHi >= 1)) throw new Error("street.guideGrowthClamp must straddle 1");
  if (!(st.growthClampLo <= st.growthClampHi)) throw new Error("street.growthClamp inverted");
  if (!(st.guideGrowthDecay >= 0 && st.guideGrowthDecay <= 1)) throw new Error("street.guideGrowthDecay outside [0,1]");
  assertPositive(st.delistPrice, "street.delistPrice");
  assertPositive(st.delistMonths, "street.delistMonths");
  if (!(st.delistWarnMonths > 0 && st.delistWarnMonths < st.delistMonths)) throw new Error("street.delistWarnMonths must be in (0, delistMonths)");
  if (!(st.secondaryDiscount >= 0 && st.secondaryDiscount < 0.5)) throw new Error("street.secondaryDiscount out of range");
  if (!(st.secondaryFee >= 0 && st.secondaryFee < 0.2)) throw new Error("street.secondaryFee out of range");
  if (!(st.secondaryMaxCapFrac > 0 && st.secondaryMaxCapFrac <= 1)) throw new Error("street.secondaryMaxCapFrac must be in (0,1]");
  if (!(st.buybackPremium >= 0 && st.buybackPremium < 0.5)) throw new Error("street.buybackPremium out of range");

  const ch = eco.channel;
  assertPositive(ch.maxMonths, "channel.maxMonths");
  if (!(ch.fillRate > 0 && ch.fillRate <= 2)) throw new Error("channel.fillRate must be in (0,2]");
  if (!(ch.shareBonus >= 0 && ch.shareBonus < 0.5)) throw new Error("channel.shareBonus out of range");
  if (!(ch.priceProtectRate >= 0 && ch.priceProtectRate <= 1)) throw new Error("channel.priceProtectRate outside [0,1]");
  if (!(ch.eolReturnRate >= 0 && ch.eolReturnRate <= 1)) throw new Error("channel.eolReturnRate outside [0,1]");
  if (!(eco.models.publicCredibilityShareBonus >= 0 && eco.models.publicCredibilityShareBonus < 0.5)) {
    throw new Error("models.publicCredibilityShareBonus out of range");
  }

  const m = eco.models;
  assertPositive(m.designEmCoeff, "models.designEmCoeff");
  assertPositive(m.verifScaleRatio, "models.verifScaleRatio");
  assertPositive(m.critLambdaCoeff, "models.critLambdaCoeff");
  assertPositive(m.yieldAlpha, "models.yieldAlpha");
  if (m.yieldGapIntro < 0) throw new Error("models.yieldGapIntro must be >= 0");
  assertPositive(m.yieldGapTauEm, "models.yieldGapTauEm");
  if (m.respinEscapeMult <= 0 || m.respinEscapeMult >= 1) throw new Error("respinEscapeMult must be in (0,1)");
  if (m.respinCoverageBoost < 0 || m.respinCoverageBoost >= 1) throw new Error("respinCoverageBoost out of range");
  assertPositive(m.blockKGatesBase, "models.blockKGatesBase");
  if (m.blockKGatesMethodExp < 0) throw new Error("models.blockKGatesMethodExp must be >= 0");
  assertPositive(m.designersPerBlock, "models.designersPerBlock");
  assertPositive(m.implPerBlock, "models.implPerBlock");
  assertPositive(m.verifPerBlock, "models.verifPerBlock");
  assertPositive(m.feasIdealStaff, "models.feasIdealStaff");
  assertPositive(m.respinFixIdealStaff, "models.respinFixIdealStaff");
  if (m.teamOverflowFrac < 0) throw new Error("models.teamOverflowFrac must be >= 0");
  if (!(m.starveFloor > 0 && m.starveFloor <= 1)) throw new Error("models.starveFloor must be in (0,1]");
  if (!(m.starveKnee >= 0 && m.starveKnee < 1)) throw new Error("models.starveKnee must be in [0,1)");
  if (!(m.designPhaseImplFrac > 0 && m.designPhaseImplFrac <= 1)) throw new Error("models.designPhaseImplFrac must be in (0,1]");
  if (!(m.implEarlyCapFrac > 0 && m.implEarlyCapFrac < 1)) throw new Error("models.implEarlyCapFrac must be in (0,1)");
  if (m.tapeinMinImplFrac < 0 || m.tapeinMinImplFrac > m.implEarlyCapFrac) {
    throw new Error("models.tapeinMinImplFrac must be in [0, implEarlyCapFrac] or tape-in is unreachable");
  }
  if (!(m.layoutDesignSupportFrac >= 0 && m.layoutDesignSupportFrac <= 1)) throw new Error("models.layoutDesignSupportFrac out of range");
  if (!(m.layoutNoDesignEff > 0 && m.layoutNoDesignEff <= 1)) throw new Error("models.layoutNoDesignEff must be in (0,1]");
  if (m.rtlReworkCoeff < 0) throw new Error("models.rtlReworkCoeff must be >= 0");
  if (!(m.rtlReworkEmFrac > 0 && m.rtlReworkEmFrac < 1)) throw new Error("models.rtlReworkEmFrac must be in (0,1)");
  if (!(m.layoutKeepFrac >= 0 && m.layoutKeepFrac < 1)) throw new Error("models.layoutKeepFrac must be in [0,1)");
  if (m.rtlReworkCoverageBoost < 0 || m.rtlReworkCoverageBoost >= 1) throw new Error("rtlReworkCoverageBoost out of range");
  if (!(m.rtlReworkEscapeMult > 0 && m.rtlReworkEscapeMult <= 1)) throw new Error("models.rtlReworkEscapeMult must be in (0,1]");
  if (!(m.offPathEffortMult >= 1)) throw new Error("models.offPathEffortMult must be >= 1");
  if (!(m.offPathFitMult > 0 && m.offPathFitMult <= 1)) throw new Error("models.offPathFitMult must be in (0,1]");
  assertPositive(m.reliabilityTauEm, "models.reliabilityTauEm");

  const sc = pack.scenario;
  if (sc.endMonthIndex <= sc.startMonthIndex) throw new Error("scenario end before start");
  // invariant 9.10: M0 supports monthly ticks only; the bi-weekly transform is M1 work (design §17.6)
  if (sc.monthsPerTick !== 1) throw new Error("monthsPerTick must be 1 in M0 (bi-weekly is M1 work per design §17.6)");
  // garage scenarios start with no company cash — the seed round funds day one
  if (sc.garage) {
    if (!Number.isInteger(sc.startingCash) || sc.startingCash < 0) throw new Error("startingCash must be a non-negative integer");
  } else {
    assertPositive(sc.startingCash, "startingCash");
    if (!Number.isInteger(sc.startingCash)) throw new Error("startingCash must be integer dollars");
  }
  for (const d of DISCIPLINES) {
    const c = sc.founders[d];
    if (!Number.isInteger(c) || c < 0) throw new Error(`founders.${d} must be non-negative integer`);
  }
  for (const id of sc.preQualifiedNodes) {
    if (!nodeIds.has(id)) throw new Error(`preQualifiedNodes: unknown node ${id}`);
  }
  if (!Number.isInteger(sc.founderShares) || sc.founderShares <= 0) {
    throw new Error("scenario.founderShares must be a positive integer");
  }
  if (sc.treeId !== undefined && !pack.trees.some((t) => t.id === sc.treeId)) {
    throw new Error(`scenario.treeId ${sc.treeId} not in pack.trees`);
  }
  if (sc.garage) {
    const g = sc.garage;
    if (g.founders.length < 1 || g.founders.length > 6) throw new Error("garage.founders must be 1-6 people");
    for (const f of g.founders) {
      if (!["technologist", "operator", "dealmaker"].includes(f.archetype)) throw new Error(`garage founder ${f.name}: bad archetype`);
      if (!DISCIPLINES.includes(f.discipline)) throw new Error(`garage founder ${f.name}: bad discipline`);
      if (!(f.savingsMonths > 0 && f.savingsMonths < 120)) throw new Error(`garage founder ${f.name}: savingsMonths out of range`);
    }
    assertPositive(g.personalBurnMonthly, "garage.personalBurnMonthly");
    if (!(g.potStart >= 0)) throw new Error("garage.potStart negative");
    if (!sc.treeId) throw new Error("garage scenarios require a treeId (the theses live on the tree)");
  }
  if (sc.devContracts) {
    const tree = pack.trees.find((t) => t.id === sc.treeId);
    const dcIds = new Set<string>();
    for (const dc of sc.devContracts) {
      if (!dc.id) throw new Error("devContract missing id");
      if (dcIds.has(dc.id)) throw new Error(`duplicate devContract id ${dc.id}`);
      dcIds.add(dc.id);
      if (!tree?.nodes.some((n) => n.id === dc.thesisId)) throw new Error(`devContract ${dc.id}: unknown thesis ${dc.thesisId}`);
      if (!dc.principal) throw new Error(`devContract ${dc.id}: principal required`);
      if (!(dc.nreMonthly >= 0 && dc.royaltyMonthly >= 0)) throw new Error(`devContract ${dc.id}: payments must be >= 0`);
      if (!(dc.nreEmMonthly >= 0)) throw new Error(`devContract ${dc.id}: nreEmMonthly must be >= 0`);
      if (!(dc.royaltyEndMonth >= dc.royaltyStartMonth)) throw new Error(`devContract ${dc.id}: royalty window inverted`);
      if (!(dc.signByMonth < dc.royaltyEndMonth || dc.royaltyMonthly === 0)) {
        throw new Error(`devContract ${dc.id}: signable after its royalty window closes`);
      }
      if ((dc.cancelMonth === undefined) !== (dc.cancelFee === undefined)) {
        throw new Error(`devContract ${dc.id}: cancelMonth and cancelFee come together`);
      }
    }
  }

  const evIds = new Set<string>();
  for (const ev of pack.events) {
    if (!ev.id) throw new Error("history event missing id");
    if (evIds.has(ev.id)) throw new Error(`duplicate history event id ${ev.id}`);
    evIds.add(ev.id);
    if (!Number.isInteger(ev.month) || ev.month < 0) throw new Error(`${ev.id}: month must be a non-negative integer`);
    if (!ev.headline || !ev.body) throw new Error(`${ev.id}: headline and body required`);
  }

  // ---- M3 sections ----
  const td = eco.talent;
  assertCurve(td.heat, "talent.heat", 0, 1);
  if (td.starNames.length === 0) throw new Error("talent.starNames empty");
  assertCurve(td.starSigningCost, "talent.starSigningCost", 0);
  if (!(td.moraleStart >= 0 && td.moraleStart <= 100)) throw new Error("talent.moraleStart out of range");
  if (!(td.moraleBaseline >= 0 && td.moraleBaseline <= 100)) throw new Error("talent.moraleBaseline out of range");
  if (!(td.moraleReversionMonthly > 0 && td.moraleReversionMonthly <= 1)) throw new Error("talent.moraleReversionMonthly out of range");
  if (!(td.compPayrollSpan >= 0 && td.compPayrollSpan < 1)) throw new Error("talent.compPayrollSpan out of range");
  if (!(td.moraleAttritionLo > 0 && td.moraleAttritionLo <= 1 && td.moraleAttritionHi >= 1)) {
    throw new Error("talent.moraleAttrition bounds must straddle 1");
  }
  if (!(td.moraleProdLo > 0 && td.moraleProdLo <= 1 && td.moraleProdHi >= 1)) {
    throw new Error("talent.moraleProd bounds must straddle 1");
  }
  if (!(td.hireFillFloor > 0 && td.hireFillFloor <= 1)) throw new Error("talent.hireFillFloor out of range");
  if (td.hireFillCompCoef < 0 || td.hireFillHeatCoef < 0) throw new Error("talent.hireFill coefs must be >= 0");
  if (!(td.poachClusterFrac > 0 && td.poachClusterFrac < 1)) throw new Error("talent.poachClusterFrac out of range");
  if (!Number.isInteger(td.poachClusterMax) || td.poachClusterMax < 1) throw new Error("talent.poachClusterMax must be a positive integer");
  if (!Number.isInteger(td.starMax) || td.starMax < 1) throw new Error("talent.starMax must be a positive integer");
  if (!(td.liftOutSizeMin >= 1 && td.liftOutSizeMax >= td.liftOutSizeMin)) throw new Error("talent.liftOutSize range invalid");
  assertPositive(td.integrationMonths, "talent.integrationMonths");
  if (!(td.integrationAttritionMult >= 1)) throw new Error("talent.integrationAttritionMult must be >= 1");

  const sd = eco.stakeholders;
  assertCurve(sd.crunch, "stakeholders.crunch", 0, 1);
  if (!(sd.foundryStart >= 0 && sd.foundryStart <= 100)) throw new Error("stakeholders.foundryStart out of range");
  if (!(sd.customerStart >= 0 && sd.customerStart <= 100)) throw new Error("stakeholders.customerStart out of range");
  if (!(sd.hotLotRelFloor > sd.penaltyRelCeil)) throw new Error("stakeholders: hotLotRelFloor must exceed penaltyRelCeil");
  if (!(sd.crunchSqueezeFactor > 0 && sd.crunchSqueezeFactor <= 1)) throw new Error("stakeholders.crunchSqueezeFactor out of range");
  if (!(sd.hotLotWaferDiscount >= 0 && sd.hotLotWaferDiscount < 0.5)) throw new Error("stakeholders.hotLotWaferDiscount out of range");
  if (!(sd.penaltyWaferMarkup >= 0 && sd.penaltyWaferMarkup < 0.5)) throw new Error("stakeholders.penaltyWaferMarkup out of range");
  if (!(sd.forecastBustDropFrac > 0 && sd.forecastBustDropFrac < 1)) throw new Error("stakeholders.forecastBustDropFrac out of range");
  if (!Number.isInteger(sd.askMaxOpen) || sd.askMaxOpen < 1) throw new Error("stakeholders.askMaxOpen must be a positive integer");
  for (const [k, v] of Object.entries({ ...sd.foundryDeltas, ...sd.customerDeltas })) {
    if (!Number.isFinite(v)) throw new Error(`stakeholders delta ${k} non-finite`);
  }

  const tl = eco.tools;
  for (const tier of ["basic", "standard", "premium"] as const) {
    const t = tl.edaTiers[tier];
    if (!t) throw new Error(`tools.edaTiers.${tier} missing`);
    assertPositive(t.costMult, `tools.edaTiers.${tier}.costMult`);
    if (!(t.methodMult > 0.5 && t.methodMult < 1.5)) throw new Error(`tools.edaTiers.${tier}.methodMult out of range`);
  }
  if (tl.edaTiers.standard.costMult !== 1 || tl.edaTiers.standard.methodMult !== 1) {
    throw new Error("tools.edaTiers.standard must be the 1.0 baseline (M2 compatibility)");
  }
  assertPositive(tl.inhouseTauEm, "tools.inhouseTauEm");
  if (!(tl.licenseSavingsMax >= 0 && tl.licenseSavingsMax < 1)) throw new Error("tools.licenseSavingsMax out of range");
  if (!(tl.prodBonusMax >= 0 && tl.prodBonusMax < 0.5)) throw new Error("tools.prodBonusMax out of range");
  if (!(tl.decayMonthlyUnstaffed > 0 && tl.decayMonthlyUnstaffed <= 1)) throw new Error("tools.decayMonthlyUnstaffed out of range");
  for (const ob of tl.obsolescence) {
    if (!DISCIPLINES.includes(ob.discipline)) throw new Error("tools.obsolescence: unknown discipline");
    if (!(ob.factor > 0 && ob.factor < 1)) throw new Error("tools.obsolescence.factor must be in (0,1)");
    if (!Number.isInteger(ob.month) || ob.month < 0) throw new Error("tools.obsolescence.month invalid");
  }

  const ma = eco.ma;
  assertPositive(ma.rivalPremium, "ma.rivalPremium");
  assertPositive(ma.distressedDiscount, "ma.distressedDiscount");
  if (!(ma.stumbleChance >= 0 && ma.stumbleChance < 1)) throw new Error("ma.stumbleChance out of range");
  if (!(ma.stumbleShareFactor > 0 && ma.stumbleShareFactor <= 1)) throw new Error("ma.stumbleShareFactor out of range");
  if (!STAGE_ORDER.includes(ma.minStageToBuy)) throw new Error("ma.minStageToBuy invalid");
  for (const d of DISCIPLINES) {
    if (!(ma.scaleTeamSize[d] >= 0)) throw new Error(`ma.scaleTeamSize.${d} invalid`);
  }
  assertPositive(ma.approachCooldownMonths, "ma.approachCooldownMonths");
  assertPositive(ma.approachExpiresMonths, "ma.approachExpiresMonths");

  const m3m = eco.models;
  if (!(m3m.rivalPressureMin > 0 && m3m.rivalPressureMin <= 1 && m3m.rivalPressureMax >= 1)) {
    throw new Error("models.rivalPressure bounds must straddle 1");
  }
  if (m3m.rivalAspExp < 0) throw new Error("models.rivalAspExp must be >= 0");
  if (!(m3m.rivalShareCoeff > 0 && m3m.rivalShareCap > 0 && m3m.rivalShareCap <= 1)) throw new Error("models.rivalShare params invalid");
  if (!(m3m.customerRelMin > 0 && m3m.customerRelMin <= 1 && m3m.customerRelMax >= 1)) {
    throw new Error("models.customerRel bounds must straddle 1");
  }

  const rivalIds = new Set<string>();
  for (const r of pack.rivals.roster) {
    if (rivalIds.has(r.id)) throw new Error(`duplicate rival id ${r.id}`);
    rivalIds.add(r.id);
    assertPositive(r.baseStrength, `${r.id}.baseStrength`);
    assertPositive(r.cadenceMonths, `${r.id}.cadenceMonths`);
    if (r.foundedMonth < 0) throw new Error(`${r.id}: negative foundedMonth`);
    if (r.deathHazardBase < 0 || r.deathHazardBase > 0.5) throw new Error(`${r.id}.deathHazardBase out of range`);
    if (r.markets.length === 0) throw new Error(`${r.id}: no markets`);
    for (const mk of r.markets) {
      if (!marketIds.has(mk.marketId)) throw new Error(`${r.id}: unknown market ${mk.marketId}`);
      if (mk.exitMonth !== undefined && mk.exitMonth <= mk.enterMonth) throw new Error(`${r.id}: exit before enter in ${mk.marketId}`);
    }
    if (r.fateWindow && !(r.fateWindow.to > r.fateWindow.from && r.fateWindow.hazard > 0)) {
      throw new Error(`${r.id}: invalid fateWindow`);
    }
  }
  const proc = pack.rivals.proc;
  if (proc.names.length === 0) throw new Error("rivals.proc.names empty");
  if (!(proc.spawnHazard >= 0 && proc.spawnHazard < 0.5)) throw new Error("rivals.proc.spawnHazard out of range");
  if (!Number.isInteger(proc.maxAlive) || proc.maxAlive < 0) throw new Error("rivals.proc.maxAlive invalid");

  const askIds = new Set<string>();
  const EFFECT_KEYS = new Set([
    "cash", "board", "foundry", "customer", "morale", "hires", "losePool", "starJoin", "starLeave",
    "waferDiscount", "volumeCommit", "shareBoost", "supportProgram", "sellCompany", "toolQualityHit",
  ]);
  for (const ask of pack.asks) {
    if (askIds.has(ask.id)) throw new Error(`duplicate ask id ${ask.id}`);
    askIds.add(ask.id);
    if (!["board", "foundry", "customer", "talent", "rival", "flavor"].includes(ask.source)) {
      throw new Error(`${ask.id}: bad source ${ask.source}`);
    }
    if (!ask.headline || !ask.body) throw new Error(`${ask.id}: headline and body required`);
    if (ask.options.length < 1 || ask.options.length > 3) throw new Error(`${ask.id}: 1-3 options required`);
    if (!Number.isInteger(ask.defaultOption) || ask.defaultOption < 0 || ask.defaultOption >= ask.options.length) {
      throw new Error(`${ask.id}: defaultOption out of range`);
    }
    if (!(ask.expiresMonths >= 1 && ask.expiresMonths <= 6)) throw new Error(`${ask.id}: expiresMonths out of range`);
    if (ask.cashScale !== undefined && !["eraCapital", "burn"].includes(ask.cashScale)) {
      throw new Error(`${ask.id}: bad cashScale`);
    }
    if (ask.trigger.minStage !== undefined && !STAGE_ORDER.includes(ask.trigger.minStage)) {
      throw new Error(`${ask.id}: bad minStage`);
    }
    if (ask.trigger.hazardMonthly !== undefined && !(ask.trigger.hazardMonthly > 0 && ask.trigger.hazardMonthly <= 1)) {
      throw new Error(`${ask.id}: hazardMonthly out of range`);
    }
    for (const opt of ask.options) {
      for (const key of Object.keys(opt.effects)) {
        if (!EFFECT_KEYS.has(key)) throw new Error(`${ask.id}: unknown effect ${key}`);
      }
      if (opt.effects.hires) {
        for (const h of opt.effects.hires) {
          if (!DISCIPLINES.includes(h.discipline) || !Number.isInteger(h.count) || h.count < 1) {
            throw new Error(`${ask.id}: bad hires effect`);
          }
        }
      }
      if (opt.effects.losePool && !DISCIPLINES.includes(opt.effects.losePool.discipline)) {
        throw new Error(`${ask.id}: bad losePool discipline`);
      }
    }
  }

  const ipIds = new Set<string>();
  for (const ip of pack.ip) {
    if (ipIds.has(ip.id)) throw new Error(`duplicate ip id ${ip.id}`);
    ipIds.add(ip.id);
    if (!["cpu", "dsp", "interface", "foundation"].includes(ip.kind)) throw new Error(`${ip.id}: bad kind`);
    if (!(ip.kGatesFrac > 0 && ip.kGatesFrac <= 0.5)) throw new Error(`${ip.id}.kGatesFrac out of range`);
    assertCurve(ip.upfront, `${ip.id}.upfront`, 0);
    if (!(ip.royaltyPct >= 0 && ip.royaltyPct < 0.2)) throw new Error(`${ip.id}.royaltyPct out of range`);
    if (!(ip.lambdaMult >= 0.8 && ip.lambdaMult <= 2)) throw new Error(`${ip.id}.lambdaMult out of range`);
    if (!["digital", "analog", "neutral"].includes(ip.pathFit)) throw new Error(`${ip.id}.pathFit invalid`);
    if (ip.availableFrom < 0) throw new Error(`${ip.id}.availableFrom negative`);
  }

  // ---- P0 sections: garage, tree config, architecture trees ----
  const gc = eco.garage;
  if (!(gc.dayJobEff > 0 && gc.dayJobEff < 1)) throw new Error("garage.dayJobEff must be in (0,1)");
  assertPositive(gc.demoStages, "garage.demoStages");
  assertPositive(gc.demoEmPerStage, "garage.demoEmPerStage");
  assertPositive(gc.discoveryEmPerSegment, "garage.discoveryEmPerSegment");
  if (!(gc.discoveryFitBonus >= 0 && gc.discoveryFitBonus < 0.3)) throw new Error("garage.discoveryFitBonus out of range");
  assertCurve(gc.consultingRatePerFm, "garage.consultingRatePerFm", 0);
  assertPositive(gc.networkingEmPerLevel, "garage.networkingEmPerLevel");
  if (!Number.isInteger(gc.warmIntroMax) || gc.warmIntroMax < 1) throw new Error("garage.warmIntroMax must be a positive integer");
  assertPositive(gc.recruitEmPerHire, "garage.recruitEmPerHire");
  if (!Number.isInteger(gc.recruitMax) || gc.recruitMax < 0) throw new Error("garage.recruitMax invalid");
  if (!(gc.dealmakerNetworkingMult >= 1 && gc.operatorRecruitingMult >= 1)) throw new Error("garage archetype mults must be >= 1");
  if (!(gc.technologistGradeShift >= 0 && gc.technologistGradeShift < 0.5)) throw new Error("garage.technologistGradeShift out of range");
  const gp = gc.pitch;
  assertCurve(gp.baseAmount, "garage.pitch.baseAmount", 0);
  assertCurve(gp.basePreMoney, "garage.pitch.basePreMoney", 0);
  assertPositive(gp.amountMin, "garage.pitch.amountMin");
  if (!(gp.amountMax > gp.amountMin)) throw new Error("garage.pitch.amountMax must exceed amountMin");
  if (!(gp.demoMult >= 0 && gp.introMult >= 0 && gp.teamPerHireMult >= 0)) throw new Error("garage.pitch multipliers negative");
  if (!(gp.coldCallFactor > 0 && gp.coldCallFactor <= 1)) throw new Error("garage.pitch.coldCallFactor out of range");
  assertPositive(gp.scoreFloor, "garage.pitch.scoreFloor");
  if (gp.valuationSigma < 0) throw new Error("garage.pitch.valuationSigma negative");
  if (!(gp.failStingFactor > 0 && gp.failStingFactor <= 1)) throw new Error("garage.pitch.failStingFactor out of range");
  assertPositive(gp.failStingMonths, "garage.pitch.failStingMonths");
  assertPositive(gp.cooldownMonths, "garage.pitch.cooldownMonths");
  assertPositive(gp.incorporationCost, "garage.pitch.incorporationCost");

  const tc = eco.tree;
  if (tc.gradeWeights.length !== 3 || tc.gradeWeights.some((w) => !(w >= 0)) || tc.gradeWeights.reduce((a, b) => a + b, 0) <= 0) {
    throw new Error("tree.gradeWeights must be 3 non-negative weights");
  }
  for (const g of ["A", "B", "C"] as const) {
    if (!(tc.gradeAttrMult[g] > 0 && tc.gradeAttrMult[g] < 2)) throw new Error(`tree.gradeAttrMult.${g} out of range`);
  }
  if (!(tc.exclusivePenaltyMult >= 1 && tc.exclusivePenaltyMult <= 3)) throw new Error("tree.exclusivePenaltyMult out of range");
  if (!(tc.frontierLagMonths >= 0 && tc.frontierLagMonths <= 60)) throw new Error("tree.frontierLagMonths out of range");
  if (!(eco.models.archFitExp > 0)) throw new Error("models.archFitExp must be positive");
  if (!(eco.models.archFitMin > 0 && eco.models.archFitMin <= 1 && eco.models.archFitMax >= 1)) {
    throw new Error("models.archFit bounds must straddle 1");
  }
  if (!(eco.models.archRatioCap >= 1)) throw new Error("models.archRatioCap must be >= 1");
  if (!(eco.models.frontierDiscount > 0 && eco.models.frontierDiscount <= 1)) {
    throw new Error("models.frontierDiscount must be in (0,1]");
  }

  const treeIds = new Set<string>();
  for (const tree of pack.trees) {
    if (treeIds.has(tree.id)) throw new Error(`duplicate tree id ${tree.id}`);
    treeIds.add(tree.id);
    const tn = new Map(tree.nodes.map((n) => [n.id, n]));
    if (tn.size !== tree.nodes.length) throw new Error(`${tree.id}: duplicate tech node ids`);
    for (const node of tree.nodes) {
      assertPositive(node.emCost, `${tree.id}.${node.id}.emCost`);
      if (node.availableMonth < 0) throw new Error(`${tree.id}.${node.id}: negative availableMonth`);
      const mixSum = DISCIPLINES.reduce((a, d) => a + (node.disciplineMix[d] ?? 0), 0);
      if (!(mixSum > 0.99 && mixSum < 1.01)) throw new Error(`${tree.id}.${node.id}.disciplineMix must sum to 1`);
      for (const p of node.prereqs) {
        if (!tn.has(p)) throw new Error(`${tree.id}.${node.id}: unknown prereq ${p}`);
      }
      for (const sp of node.softPrereqs ?? []) {
        if (!tn.has(sp.id)) throw new Error(`${tree.id}.${node.id}: unknown softPrereq ${sp.id}`);
        if (!(sp.discount > 0 && sp.discount < 1)) throw new Error(`${tree.id}.${node.id}: softPrereq discount out of range`);
      }
      for (const cr of node.creditsTo ?? []) {
        if (!tn.has(cr.id)) throw new Error(`${tree.id}.${node.id}: unknown creditsTo ${cr.id}`);
        if (!(cr.frac > 0 && cr.frac <= 1)) throw new Error(`${tree.id}.${node.id}: creditsTo frac out of range`);
      }
      if (node.attrs) {
        for (const [k, v] of Object.entries(node.attrs)) {
          if (!ATTR_KEYS.includes(k as (typeof ATTR_KEYS)[number])) throw new Error(`${tree.id}.${node.id}.attrs: unknown dimension ${k}`);
          if (!(typeof v === "number" && Number.isFinite(v) && v >= 0)) throw new Error(`${tree.id}.${node.id}.attrs.${k} invalid`);
        }
      }
      if (node.obsolescence && !(node.obsolescence.factor > 0 && node.obsolescence.factor < 1)) {
        throw new Error(`${tree.id}.${node.id}: obsolescence factor must be in (0,1)`);
      }
      if (node.pitchAppeal) assertCurve(node.pitchAppeal, `${tree.id}.${node.id}.pitchAppeal`, 0);
      if (node.cashCost) assertCurve(node.cashCost, `${tree.id}.${node.id}.cashCost`, 0);
      if (node.tapeoutCoverageBoost !== undefined && !(node.tapeoutCoverageBoost > 0 && node.tapeoutCoverageBoost < 0.8)) {
        throw new Error(`${tree.id}.${node.id}.tapeoutCoverageBoost out of range`);
      }
      if (node.gradeWeights && (node.gradeWeights.length !== 3 || node.gradeWeights.some((w) => !(w >= 0)))) {
        throw new Error(`${tree.id}.${node.id}.gradeWeights invalid`);
      }
      for (const seg of node.segmentAccess ?? []) {
        if (!marketIds.has(seg)) throw new Error(`${tree.id}.${node.id}.segmentAccess: unknown market ${seg}`);
      }
    }
    // the DAG must be acyclic and prereqs must not point forward in time
    const visiting = new Set<string>();
    const done = new Set<string>();
    const visit = (id: string): void => {
      if (done.has(id)) return;
      if (visiting.has(id)) throw new Error(`${tree.id}: prereq cycle through ${id}`);
      visiting.add(id);
      const node = tn.get(id)!;
      for (const p of node.prereqs) {
        if (tn.get(p)!.availableMonth > node.availableMonth) {
          throw new Error(`${tree.id}.${id}: prereq ${p} becomes available later than the node itself`);
        }
        visit(p);
      }
      visiting.delete(id);
      done.add(id);
    };
    for (const node of tree.nodes) visit(node.id);
  }
  // segment tech gates must reference nodes on SOME tree in the pack
  const allTechIds = new Set(pack.trees.flatMap((t) => t.nodes.map((n) => n.id)));
  for (const mk of pack.markets) {
    for (const gate of mk.techGatesAnyOf ?? []) {
      if (!allTechIds.has(gate)) throw new Error(`${mk.id}.techGatesAnyOf: unknown tech node ${gate}`);
    }
  }
}

export function getNode(content: ContentPack, id: string): NodeDef {
  const n = content.nodes.find((x) => x.id === id);
  if (!n) throw new Error(`unknown node ${id}`);
  return n;
}

export function getMarket(content: ContentPack, id: string): MarketDef {
  const m = content.markets.find((x) => x.id === id);
  if (!m) throw new Error(`unknown market ${id}`);
  return m;
}

/** Index of a node in the availability-sorted roadmap. */
export function nodeIndex(content: ContentPack, id: string): number {
  const i = content.nodes.findIndex((x) => x.id === id);
  if (i < 0) throw new Error(`unknown node ${id}`);
  return i;
}

/** Index of the newest node available at least `lagMonths` before `monthIndex` (min 0). */
export function expectedNodeIndex(content: ContentPack, monthIndex: number, lagMonths: number): number {
  let idx = 0;
  for (let i = 0; i < content.nodes.length; i++) {
    if (content.nodes[i]!.availableMonth <= monthIndex - lagMonths) idx = i;
  }
  return idx;
}
